<script setup lang="ts">
import { onMounted, shallowRef, ref, reactive } from "vue";
import { AgGridVue } from "ag-grid-vue3";
import {
  themeQuartz,
  type GridApi,
  type GridOptions,
  type GridReadyEvent,
} from "ag-grid-enterprise";
import {useCharterUsersStore} from "@/charter/stores/charter-users-store.ts";
import {useCharterRoleStore} from "@/charter/stores/charter-role-store.ts";
import {UserCreateRequest} from "@/api/client/models/index.ts";

const charterUsersStore = useCharterUsersStore();
const charterRoleStore = useCharterRoleStore();

const myTheme = themeQuartz.withParams({});

const gridOptions: GridOptions = {
  pagination: true,
  autoSizeStrategy: {
    type: "fitGridWidth",
    defaultMinWidth: 100,
  },
  defaultColDef: {
    resizable: false,
  },
  columnDefs: [
    { field: "email", headerName: "Email", filter: true },
    { field: "firstName", headerName: "First Name", filter: true },
    { field: "lastName", headerName: "Last Name", filter: true },
    { field: "created", headerName: "Role", filter: true },
  ],
};

const gridApi = shallowRef<GridApi | null>(null);
const showAddUserForm = ref(false);
const isSubmitting = ref(false);

const newUser = reactive<UserCreateRequest>({
  email: "",
  allotmentRoleId: 1, // Default to first role
  charterRoleId: 1, // Default to first role
  tourOperatorId: null,
  tourOperatorRoleId: null
});

// Allotment roles (from mock data)
const allotmentRoles = [
  { id: 1, name: "Admin" },
  { id: 2, name: "Finance" },
  { id: 3, name: "Tour Operator" },
  { id: 4, name: "Read-only admin" },
  { id: 5, name: "HCC read-only" }
];

// Tour operator roles (from mock data)
const tourOperatorRoles = [
  { id: 1, name: "Alt Access" },
  { id: 2, name: "Orly Reporting" },
  { id: 3, name: "ORly Allotment Changes" }
];

// Tour operators (simplified for demo)
const tourOperators = [
  { id: 1, name: "Tour Operator 1" },
  { id: 2, name: "Tour Operator 2" },
  { id: 3, name: "Tour Operator 3" }
];

onMounted(async () => {
  await charterUsersStore.fetchUsers();
  await charterRoleStore.fetchRoles();
});

function onGridReady(params: GridReadyEvent) {
  gridApi.value = params.api;
}

function refreshData() {
  charterUsersStore.fetchUsers();
}

function toggleAddUserForm() {
  showAddUserForm.value = !showAddUserForm.value;
}

async function addUser() {
  if (!newUser.email) {
    return; // Basic validation
  }

  isSubmitting.value = true;
  try {
    await charterUsersStore.addUser(newUser);
    // Reset form
    newUser.email = "";
    newUser.allotmentRoleId = 1;
    newUser.charterRoleId = 1;
    newUser.tourOperatorId = null;
    newUser.tourOperatorRoleId = null;
    showAddUserForm.value = false;
  } catch (error) {
    console.error("Failed to add user:", error);
  } finally {
    isSubmitting.value = false;
  }
}
</script>

<template>
  <div class="flex h-full flex-col gap-4">
    <div class="flex items-center justify-between">
      <h1>User management</h1>
      <div class="flex gap-2">
        <p-button link @click="toggleAddUserForm">
          <span class="material-icons" title="add">add</span>
        </p-button>
        <p-button link @click="refreshData" :loading="charterUsersStore.isLoading">
          <span class="material-icons" title="refresh">refresh</span>
        </p-button>
      </div>
    </div>

    <div v-if="charterUsersStore.hasError" class="p-message p-message-error">
      <div class="p-message-wrapper">
        <span class="p-message-icon pi pi-times-circle"></span>
        <div class="p-message-text">
          {{ charterUsersStore.error }}
        </div>
        <button
          class="p-message-close p-link"
          @click="charterUsersStore.clearError"
        >
          <span class="pi pi-times"></span>
        </button>
      </div>
    </div>

    <!-- Add User Form -->
    <div v-if="showAddUserForm" class="mb-3 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <div class="mb-4 text-lg font-semibold">Add New User</div>
      <div>
        <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div class="space-y-2">
            <label for="email">Email*</label>
            <p-input-text id="email" v-model="newUser.email" required />
          </div>

          <div class="space-y-2">
            <label for="allotmentRoleId">Allotment Role*</label>
            <p-dropdown
              id="allotmentRoleId"
              v-model="newUser.allotmentRoleId"
              :options="allotmentRoles"
              optionLabel="name"
              optionValue="id"
              placeholder="Select Allotment Role"
              required
            />
          </div>

          <div class="space-y-2">
            <label for="charterRoleId">Charter Role*</label>
            <p-dropdown
              id="charterRoleId"
              v-model="newUser.charterRoleId"
              :options="charterRoleStore.roles"
              optionLabel="name"
              optionValue="id"
              placeholder="Select Charter Role"
              required
            />
          </div>

          <div class="space-y-2">
            <label for="tourOperatorId">Tour Operator</label>
            <p-dropdown
              id="tourOperatorId"
              v-model="newUser.tourOperatorId"
              :options="tourOperators"
              optionLabel="name"
              optionValue="id"
              placeholder="Select Tour Operator"
            />
          </div>

          <div class="space-y-2">
            <label for="tourOperatorRoleId">Tour Operator Role</label>
            <p-dropdown
              id="tourOperatorRoleId"
              v-model="newUser.tourOperatorRoleId"
              :options="tourOperatorRoles"
              optionLabel="name"
              optionValue="id"
              placeholder="Select Tour Operator Role"
            />
          </div>
        </div>

        <div class="mt-3 flex justify-end gap-2">
          <p-button
            label="Cancel"
            icon="pi pi-times"
            severity="secondary"
            outlined
            @click="toggleAddUserForm"
          />
          <p-button
            label="Add User"
            icon="pi pi-check"
            severity="primary"
            @click="addUser"
            :loading="isSubmitting"
          />
        </div>
      </div>
    </div>

    <AgGridVue
      :rowData="charterUsersStore.users"
      :loading="charterUsersStore.isLoading"
      :gridOptions="gridOptions"
      ref="gridRef"
      :theme="myTheme"
      style="width: 100%; height: 100%"
      @grid-ready="onGridReady"
    />
  </div>
</template>

<style scoped lang="scss">
h1 {
  margin: 0 !important;
}

.p-button {
  min-height: fit-content !important;
}

.p-button-icon-left {
  margin-right: 0.4375rem !important;
}

.p-button-label {
  font-weight: 700 !important;
}

.p-button-outlined {
  background-color: white !important;
  color: rgb(30, 41, 59) !important;
  font-size: 14px;
  line-height: 21px !important;
  padding: 6px 10px;
  border-radius: 6px;
  border-color: #e2e8f0 !important;
}

.p-button-outlined:hover {
  background-color: var(--p-button-outlined-secondary-hover-background) !important;
}

.p-button-outlined:active {
  background-color: var(--p-button-outlined-secondary-active-background) !important;
}

.p-message {
  margin: 0;
}
</style>
